import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowLeft, Eye } from 'lucide-react';
import { Button } from '../../components/Button';
import { Card, CardHeader } from '../../components/Card';
import { Banner } from '../../components/Banner';
import { Input } from '../../components/Input';
import { Money } from '../../components/Money';

/** Schedule a price change — single-page form with notice options & email preview. */
export function SchedulePriceChange() {
  const [newPrice, setNewPrice] = useState('1700');
  const [date, setDate] = useState('2026-07-01');
  const [strategy, setStrategy] = useState<'grandfather' | 'next_renewal' | 'immediate'>('grandfather');
  const [emailNotice, setEmailNotice] = useState(true);
  const [bannerNotice, setBannerNotice] = useState(true);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-3xl mx-auto">
      <Link to="/admin/packages" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 mb-4 transition">
        <ArrowLeft className="w-4 h-4" />
        Back to package
      </Link>

      <div className="mb-6">
        <p className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-1.5">Pricing</p>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
          Schedule a price change
        </h1>
        <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">Pro · BDT Monthly</p>
      </div>

      <div className="space-y-6">
        <Card className="p-5 sm:p-6">
          <CardHeader title="Pricing change" />
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">Current price</label>
              <div className="px-3 py-2.5 rounded-md bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700 text-sm">
                <Money minor={150000} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50 font-medium" />
                <span className="text-zinc-500 dark:text-zinc-400 ml-1.5">/ month</span>
              </div>
            </div>
            <Input
              label="New price"
              required
              value={newPrice}
              onChange={(e) => setNewPrice(e.target.value)}
              prefix={<span className="text-sm">৳</span>}
              suffix={<span className="text-xs">/ month</span>}
            />
          </div>
          <div className="mt-4">
            <Input
              label="Effective date"
              required
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              hint="60 days from now"
            />
          </div>
        </Card>

        <Card className="p-5 sm:p-6">
          <CardHeader title="Affects new subscribers" subtitle={`New sign-ups from ${date} see ৳${newPrice}.`} />
          <Banner tone="info" title="No action needed for new sign-ups">
            They'll see the new price automatically once the effective date passes.
          </Banner>
        </Card>

        <Card className="p-5 sm:p-6">
          <CardHeader
            title="Affects existing subscribers"
            subtitle="67 active on this price"
          />
          <div className="space-y-3">
            <StrategyRadio
              checked={strategy === 'grandfather'}
              onChange={() => setStrategy('grandfather')}
              title="Grandfather"
              description="Keep existing subscribers at ৳1,500 forever (until they actively change plans). Industry default — Stripe/Chargebee/Recurly all do this."
              tone="recommended"
            />
            <StrategyRadio
              checked={strategy === 'next_renewal'}
              onChange={() => setStrategy('next_renewal')}
              title="Migrate at next renewal"
              description="Each subscriber moves to the new price on their personal renewal day. Rolling cutover, less surprise."
            />
            <StrategyRadio
              checked={strategy === 'immediate'}
              onChange={() => setStrategy('immediate')}
              title="Migrate immediately on effective date"
              description="All 67 subscribers move on the same day. Use sparingly — risk of churn."
              tone="danger"
            />
          </div>
        </Card>

        <Card className="p-5 sm:p-6">
          <CardHeader title="Notice" subtitle="Tell subscribers ahead of time." />
          <div className="space-y-2">
            <label className="flex items-start gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
              <input type="checkbox" checked={emailNotice} onChange={(e) => setEmailNotice(e.target.checked)} className="mt-0.5 rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand" />
              <span>Send 60-day advance notice email to affected subscribers</span>
            </label>
            <label className="flex items-start gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
              <input type="checkbox" checked={bannerNotice} onChange={(e) => setBannerNotice(e.target.checked)} className="mt-0.5 rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand" />
              <span>Show in-app banner from 01 Jun 2026</span>
            </label>
          </div>
        </Card>

        <Card className="p-5 sm:p-6">
          <CardHeader
            title="Preview email"
            action={
              <Button variant="ghost" size="sm" iconLeft={<Eye className="w-4 h-4" />}>Preview</Button>
            }
          />
          <div className="rounded-lg border border-zinc-200 dark:border-zinc-600 bg-zinc-50 dark:bg-zinc-800/40 p-4 text-sm">
            <div className="text-xs uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-1">Subject</div>
            <div className="text-zinc-900 dark:text-zinc-100 font-medium">Pricing update for your Pro plan</div>
            <div className="mt-3 text-zinc-700 dark:text-zinc-200 leading-relaxed">
              Hi {'{first_name}'},
              <br /><br />
              Starting <span className="font-medium tabular-nums">01 July 2026</span>, the Pro plan price changes from <Money minor={150000} hideCents tone="neutral" /> to <Money minor={170000} hideCents tone="neutral" />/month.
              {strategy === 'grandfather' && (
                <> Your current subscription is grandfathered — you will continue paying <Money minor={150000} hideCents tone="neutral" /> unless you change plans.</>
              )}
              <br /><br />
              Questions? Reply to this email.
            </div>
          </div>
        </Card>

        <div className="flex flex-col sm:flex-row sm:justify-between gap-3">
          <Link to="/admin/packages">
            <Button variant="ghost">Cancel</Button>
          </Link>
          <Button>Schedule change</Button>
        </div>
      </div>
    </div>
  );
}

function StrategyRadio({
  checked,
  onChange,
  title,
  description,
  tone = 'normal',
}: {
  checked: boolean;
  onChange: () => void;
  title: string;
  description: string;
  tone?: 'normal' | 'recommended' | 'danger';
}) {
  const ringCls =
    checked
      ? 'border-brand bg-brand-subtle dark:border-brand-dm dark:bg-brand-dm-subtle/30'
      : 'border-zinc-200 dark:border-zinc-700 hover:border-zinc-300 dark:hover:border-zinc-600';
  return (
    <label className={`block p-4 rounded-lg border-2 cursor-pointer transition ${ringCls}`}>
      <div className="flex items-start gap-3">
        <input type="radio" name="strategy" checked={checked} onChange={onChange} className="mt-1 text-brand focus:ring-brand" />
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-zinc-950 dark:text-zinc-50">{title}</span>
            {tone === 'recommended' && <span className="text-[10px] uppercase tracking-wide px-1.5 py-px rounded bg-emerald-600 text-white">Recommended</span>}
            {tone === 'danger' && <span className="text-[10px] uppercase tracking-wide px-1.5 py-px rounded bg-rose-600 text-white">Risk</span>}
          </div>
          <p className="text-sm text-zinc-600 dark:text-zinc-300 mt-0.5">{description}</p>
        </div>
      </div>
    </label>
  );
}
